import { useRef, useState, useLayoutEffect, useCallback, useEffect, } from 'react';
import { getSmallestBreakpoint } from './helpers/getSmallestBreakpoint';
import { findLargestMatchingBreakpoint } from './helpers/findLargestMatchingBreakpoint';
import { createBreakpointQueryArray } from './helpers/createBreakpointQueryArray';
import { createMediaQueryListener } from './MediaQueryListener';
/* istanbul ignore next */
var globalWindow = typeof window !== 'undefined' ? window : null;
var useIsomorphicLayoutEffect = globalWindow ? useLayoutEffect : useEffect;
/**
 * Detects breakpoints using media queries
 * - [matchMedia]( https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia)
 * - [MediaQueryList](https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList)
 *
 * Might not work for some older browsers
 * @private for internal use
 * @param props
 * @return
 */
export function useMatchMediaBreakpoints(props) {
    var breakpoints = props.breakpoints, breakpointUnit = props.breakpointUnit, _a = props.defaultBreakpoint, defaultBreakpoint = _a === void 0 ? getSmallestBreakpoint(breakpoints) : _a;
    /** A ref holding the currently matched breakpoints */
    var matchedBreakpoints = useRef({});
    /** Flag a breakpoint as matching */
    var updateBreakpointMatch = useCallback(function (id, matches) {
        matchedBreakpoints.current[id] = matches;
    }, []);
    /** Computes the new breakpoint */
    var computeNewBreakpoint = useCallback(function () {
        var match = findLargestMatchingBreakpoint(breakpoints, matchedBreakpoints.current, defaultBreakpoint);
        setCurrentBreakpoint(match);
    }, [breakpoints, defaultBreakpoint]);
    // The current matching breakpoint, the return value of the hook
    var _b = useState(function () {
        return findLargestMatchingBreakpoint(breakpoints, matchedBreakpoints.current, defaultBreakpoint);
    }), currentBreakpoint = _b[0], setCurrentBreakpoint = _b[1];
    useIsomorphicLayoutEffect(function () {
        var listeners = [];
        // reset the ref
        matchedBreakpoints.current = {};
        if (globalWindow && typeof globalWindow.matchMedia === 'function') {
            var queries = createBreakpointQueryArray(breakpoints, breakpointUnit);
            queries.forEach(function (_a) {
                var name = _a.name, query = _a.query;
                var mediaQueryList = globalWindow.matchMedia(query);
                // set initial match
                updateBreakpointMatch(name, mediaQueryList.matches);
                // update matches
                listeners.push(createMediaQueryListener(mediaQueryList, function (ev) {
                    updateBreakpointMatch(name, ev.matches);
                    computeNewBreakpoint();
                }));
            });
        }
        // after we determined all matches, compute new breakpoint
        computeNewBreakpoint();
        // cleanup effects removes listeners
        return function () {
            // if no window exists, then no listeners will have been added
            for (var _i = 0, listeners_1 = listeners; _i < listeners_1.length; _i++) {
                var listener = listeners_1[_i];
                listener.detach();
            }
        };
    }, [
        breakpointUnit,
        breakpoints,
        defaultBreakpoint,
        computeNewBreakpoint,
        updateBreakpointMatch,
    ]);
    return currentBreakpoint;
}
//# sourceMappingURL=useMatchMediaBreakpoints.js.map